import operationInDividends from './operations-in-dividends.png'
import visitor from './visitor.png'

const content = `<p>The Visitor design pattern helps separate the code of an operation from the code of the data that the operation manipulates.</p>

<p>Visitor is useful when there are several types of operations that can apply on several types of data. It allows to make the code of the data independent from the code of the operations. This <a href="/high-cohesion">increases cohesion</a> of the code and <a href="/low-coupling">reduces coupling</a>.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate.</p>

<p>In the financial industry, dividends are amounts of money paid to people that hold company shares. An important topic around dividends is to estimate them and see what it looks like to make those estimations vary.</p>

<p>A simple dividend is expressed in absolute, like 5 EUR. A percentage dividend is expressed in percentage of the share price, for example 5% of the share price. Let’s have two classes to represent those two types of dividends, one for absolute dividends:</p>

<pre><code class="language-ruby">class AbsoluteDividend
  attr_reader :value

  def initialize(value)
    @value = value
  end
end</code></pre>

<p>And one for percentage dividends:</p>

<pre><code class="language-ruby">class PercentageDividend
  attr_reader :value, :share_price

  def initialize(value, share_price)
    @value = value
    @share_price = share_price
  end
end</code></pre>

<p>We want to apply a variation on those dividends (ultimately to measure the impact of the variation on other factors, outside of our scope here) and there are two types of variations:</p>

<ul><li>absolute variations, like + 1 EUR</li><li>relative variations, like + 1%</li></ul>

<p>Any types of variation can be applied to any types of dividend, with different formulae:</p>

<ul><li>absolute variation on an absolute dividend is dividend value + 1 EUR</li><li>relative variation on an absolute dividend is dividend value * 1.01</li><li>absolute variation on a percentage dividend is dividend value + 1 / share_price</li><li>relative variation on a percentage dividend is dividend value * 1.01</li></ul>

<p>In which part of the code should we put those formulae?</p>

<h2 id="Mixing-operations-and-data">Mixing operations and data</h2>

<p>One option is to put the operations directly in the dividend classes. In the absolute dividend:</p>

<pre><code class="language-ruby">class AbsoluteDividend
  def initialize(value)
    @value = value
  end

  def applyAbsoluteVariation
    @value + 1
  end

  def applyRelativeVariation
    @value * 1.01
  end
end</code></pre>

<p>And in the percentage dividend:</p>

<pre><code class="language-ruby">class PercentageDividend
  def initialize(value, share_price)
    @value = value
    @share_price = share_price
  end

  def applyAbsoluteVariation
    @value + (1. / @share_price)
  end

  def applyRelativeVariation
    @value * 1.01
  end
end</code></pre>

<p>In this code, the same class represents the data and also contains the code of the operations. The dividend classes mix two concerns: representing a dividend and calculating its variations:</p>

<img src=${operationInDividends} alt="operations-in-dividends" />

<p>In the above code, the classes are so small that this lack of cohesion is not a problem. But real dividend classes have a lot more methods, and variations are a lot more complex than the above formulae. In such cases, it can be useful to extract the operations in separate classes.</p>

<p>Also the classes as they are don’t allow for generic code to have an operation and a variation and apply them without knowing their type. As in this generic code:</p>

<pre><code class="language-ruby">def my_algorithm(dividend, variation) # could be any type of dividend and any type of variation
  dividend.applyVariation(variation)
end</code></pre>

<p>Let’s illustrate how Visitor can help achieve all this, and keep this small example to illustrate its mechanics.</p>

<h2 id="Extracting-operations-with-a-Visitor">Extracting operations with a Visitor</h2>

<p>In a visitor, types of operations are grouped in classes, and each class knows how to apply a certain operation to all types of data.</p>

<p>For example, we’d have an <code>AbsoluteVariation</code> class:</p>

<pre><code class="language-ruby">class AbsoluteVariation
  def applyOnAbsoluteDividend(absoluteDividend)
    absoluteDividend.value + 1
  end

  def applyOnPercentageDividend(percentageDividend)
    percentageDividend.value + (1. / percentageDividend.share_price)
  end
end</code></pre>

<p>And a <code>RelativeVariation</code> class:</p>

<pre><code class="language-ruby">class RelativeVariation
  def applyOnAbsoluteDividend(absoluteDividend)
    absoluteDividend.value * 1.01
  end

  def applyOnPercentageDividend(percentageDividend)
    percentageDividend.value * 1.01
  end
end</code></pre>

<p>Given an operation and a type of data, we need to make the execution end up in the code implementing the correct formula. The technique to do this is a bit convoluted. First we add a <code>visit</code> method on the operation. For example on <code>AbsoluteVariation</code>:</p>

<pre><code class="language-ruby">class AbsoluteVariation
  def visit(dividend)
    dividend.accept(self)
  end

  def applyOnAbsoluteDividend(absoluteDividend)
    absoluteDividend.value + 1
  end

  def applyOnPercentageDividend(percentageDividend)
    percentageDividend.value + (1. / percentageDividend.share_price)
  end
end</code></pre>

<p>It can be called this way:</p>

<pre><code class="language-ruby">AbsoluteVariation.new.visit(AbsoluteDividend.new(5))</code></pre>

<p>The <code>visit</code> method calls an <code>accept</code> method on the right data class (here <code>AbsoluteDividend</code>). So we implement an <code>accept</code> method on the data class:</p>

<pre><code class="language-ruby">class AbsoluteDividend
  def accept(operation)
    operation.applyOnAbsoluteDividend(self)
  end

  attr_reader :value

  def initialize(value)
    @value = value
  end
end</code></pre>

<p>The <code>accept</code> method calls back the operation on the method that corresponds to the type of data.</p>

<p>All this is a lot of code and techniques. Let’s take several steps back to understand what the Visitor pattern allowed us to achieve, and when it’s useful to apply it.</p>

<h2 id="First-step-back:-Polymorphism-and-double-dispatch">First step back: Polymorphism and double dispatch</h2>

<p>An interesting aspect of visitor is that it resolves two polymorphic calls at a time. Let’s see what this means in detail.</p>

<p>Let’s go back to our generic piece of code that can work with any type of variation and any type of dividend. With the naming of the Visitor pattern it looks like this:</p>

<pre><code class="language-ruby">def my_algorithm(dividend, variation) # could be any type of dividend and any type of variation
  dividend.visit(variation)
end</code></pre>

<p>There are two polymorphic concepts in this code: the variation (that can be either <code>AbsoluteVariation</code> or <code>RelativeVariation</code>) and the dividend (that can be <code>AbsoluteDividend</code>or <code>PercentageDividend</code>).</p>

<p>Most languages don’t allow to resolve two polymorphic calls at the same time. We therefore have to make two polymorphic calls: one to identify the type of operation, and one to identify the type of data.</p>

<p>Calling <code>visit</code> ends up in one specific variation class. This is a polymorphic resolution that reaches the <code>visit</code> function of that class:</p>

<pre><code class="language-ruby">class AbsoluteVariation
  def visit(dividend)
    dividend.accept(self)
  end
  
  # ...</code></pre>

<p>Then in <code>visit</code>, the <code>dividend</code> parameter is can be any type of dividend. But calling <code>accept</code> ends up in one specific class of dividend. This is another polymorphic resolution.</p>

<p>Most languages can only resolve one polymorphic parameter at a time. In language design this is called Single Dispatch. The Visitor pattern, with its two successive polymorphic resolutions in <code>visit</code> and <code>accept</code>, acts like if there were two polymorphic resolutions at a time (the variation type and the dividend type). This is called Double Dispatch.</p>

<p>Visitor is a way to emulate double dispatch in languages that only support single dispatch.</p>

<h2 id="Second-step-back:-High-cohesion">Second step back: High cohesion</h2>

<p>The Visitor pattern added a lot of code and technical functions. Let’s take a step back on how the concerns are now distributed in the code:</p>

<img src=${visitor} alt="visitor" />

<p>Each class has one very specific goal. The Visitor pattern allowed to group concerns together and have classes with <a href="/high-cohesion">High cohesion</a>.</p>

<h2 id="Last-step-back:-when-is-this-useful?">Last step back: when is this useful?</h2>

<p>The Visitor pattern has the advantages of increasing cohesion in code and allowing polymorphism on operations and data, but it comes with all its machinery of <code>accept</code> and <code>visit</code> functions along with added classes.</p>

<p>When is this better than the simple design we started with?</p>

<p>First, if there is a lot of code in the operations and in the data classes, there is a risk of low cohesion, and Visitor can help maintain high cohesion.</p>

<p>Second, Visitor becomes more useful if there are several types of operations. In our example we focused on dividend variations. But there are many other operations that act differently depending on dividend types (estimation of future share price, simulation of crises, etc.), and the Visitor pattern prevents the dividend classes to carry the code of all those operations.</p>

<p>Third, when we need generic code to manipulate polymorphic operations on polymorphic types, Visitor allows to represent operations and data as polymorphic objects, and to resolve both in one call as in double dispatch. In our example, Visitor would be useful for generic code that doesn’t know the precise type of dividend nor the precise type of variation.</p>

<p>But in code that doesn’t have multiple types of operations and that doesn’t need double dispatch, it’s often better to stick to straightforward code instead of putting in place the elaborate machinery of a Visitor pattern.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Visitor allows to separate the code of operations from the code of data</li><li>It increases the cohesion of the data classes by removing the operation concerns from them</li><li>It resolves the type of operation in <code>visit</code> and the type of data in <code>accept</code></li><li>It emulates double dispatch and allows generic operations on generic data</li><li>If the code is small and not generic, the advantages of Visitor are not worth its overhead</li></ul>
</div>`

export default content
